import React, { useState } from 'react';
import {
  Alert,
  Box,
  Typography,
  TextField,
  Button,
  IconButton,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormControlLabel,
  Checkbox,
  Divider,
  Paper,
  Chip,
  useTheme,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import { tokens } from '../../theme';
import 'mathlive';
import {
  defaultMultilineConfig,
  generateRowId,
  getRowType,
  ROW_TYPES,
} from './multilineUtils';

/**
 * MultilineAdmin Component
 * Admin interface for configuring multiline-based homework questions.
 * Allows setting input type, row limits, grading mode, and correct answers.
 */
const MultilineAdmin = ({
  config = defaultMultilineConfig,
  onConfigChange,
  correctAnswer = { rows: [] },
  onAnswerChange,
}) => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);

  const [rowLabelsText, setRowLabelsText] = useState(
    (config.rowLabels || []).join(', ')
  );

  const updateConfig = (updates) => {
    onConfigChange?.({ ...config, ...updates });
  };

  const dualInput = config.inputType === 'mathlive' && config.allowTextInput === true;

  // --- Correct answer management ---
  const addCorrectRow = () => {
    const newRow = { id: generateRowId(), value: '' };
    if (dualInput) newRow.type = ROW_TYPES.MATH;
    const newRows = [...(correctAnswer.rows || []), newRow];
    onAnswerChange?.({ rows: newRows });

    // Auto-bump row limits so students can see and submit every correct row
    // by default — keeps minRows/maxRows in lock-step with the answer count
    // as the admin builds the answer key. We only grow, never shrink.
    const newCount = newRows.length;
    const currentMin = config.minRows || 1;
    const currentMax = config.maxRows || 0;
    const configUpdates = {};
    if (currentMin < newCount) configUpdates.minRows = newCount;
    if (currentMax > 0 && currentMax < newCount) configUpdates.maxRows = newCount;
    if (Object.keys(configUpdates).length > 0) {
      onConfigChange?.({ ...config, ...configUpdates });
    }
  };

  const removeCorrectRow = (rowId) => {
    const newRows = (correctAnswer.rows || []).filter(r => r.id !== rowId);
    onAnswerChange?.({ rows: newRows });
  };

  const updateCorrectRow = (rowId, value) => {
    const newRows = (correctAnswer.rows || []).map(r =>
      r.id === rowId ? { ...r, value } : r
    );
    onAnswerChange?.({ rows: newRows });
  };

  // For dual-input correct rows, write the active type alongside the value
  // so the row type tracks whichever field the admin is filling in.
  const updateCorrectRowTyped = (rowId, type, value) => {
    const newRows = (correctAnswer.rows || []).map(r =>
      r.id === rowId ? { ...r, type, value } : r
    );
    onAnswerChange?.({ rows: newRows });
  };

  const handleRowLabelsChange = (text) => {
    setRowLabelsText(text);
    const labels = text.split(',').map(s => s.trim()).filter(Boolean);
    updateConfig({ rowLabels: labels });
  };

  return (
    <Box sx={{ width: '100%' }}>
      <Typography variant="subtitle1" fontWeight="bold" sx={{ mb: 2 }}>
        Multiline Configuration
      </Typography>

      {/* Configuration Panel */}
      <Paper sx={{ p: 2, mb: 2, backgroundColor: colors.primary[600] }}>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, mb: 2 }}>
          {/* Input Type */}
          <FormControl size="small" sx={{ minWidth: 200 }}>
            <InputLabel>Input Type</InputLabel>
            <Select
              value={config.inputType || 'mathlive'}
              label="Input Type"
              renderValue={(v) => v === 'text' ? 'Text' : 'Math (MathLive)'}
              onChange={(e) => {
                const nextInputType = e.target.value;
                // allowTextInput only makes sense in mathlive mode — clear it
                // when switching to text so the saved config doesn't carry an
                // orphan flag that has no UI effect.
                const updates = { inputType: nextInputType };
                if (nextInputType === 'text' && config.allowTextInput) {
                  updates.allowTextInput = false;
                }
                updateConfig(updates);
              }}
            >
              <MenuItem value="mathlive">
                <Box>
                  <Typography variant="body2">Math (MathLive)</Typography>
                  <Typography variant="caption" sx={{ display: 'block', color: colors.grey[400] }}>
                    Equations, fractions, square roots, etc. Use for numeric or formula answers.
                  </Typography>
                </Box>
              </MenuItem>
              <MenuItem value="text">
                <Box>
                  <Typography variant="body2">Text</Typography>
                  <Typography variant="caption" sx={{ display: 'block', color: colors.grey[400] }}>
                    Plain prose. Use for word answers like "true", "no solution", or short labels.
                  </Typography>
                </Box>
              </MenuItem>
            </Select>
          </FormControl>

          {/* Grading Mode */}
          <FormControl size="small" sx={{ minWidth: 220 }}>
            <InputLabel>Grading Mode</InputLabel>
            <Select
              value={config.gradingMode || 'manual'}
              label="Grading Mode"
              renderValue={(v) => v === 'exactSet'
                ? 'Exact Set Match'
                : v === 'unorderedMatch'
                  ? 'Unordered Match'
                  : 'Manual (Teacher grades)'}
              onChange={(e) => updateConfig({ gradingMode: e.target.value })}
            >
              <MenuItem value="manual">
                <Box>
                  <Typography variant="body2">Manual (Teacher grades)</Typography>
                  <Typography variant="caption" sx={{ display: 'block', color: colors.grey[400] }}>
                    No auto-grading — teacher reviews each submission by hand.
                  </Typography>
                </Box>
              </MenuItem>
              <MenuItem value="exactSet">
                <Box>
                  <Typography variant="body2">Exact Set Match</Typography>
                  <Typography variant="caption" sx={{ display: 'block', color: colors.grey[400] }}>
                    Student must provide ALL correct answers and ONLY those. Count must match. Order doesn't matter.
                  </Typography>
                </Box>
              </MenuItem>
              <MenuItem value="unorderedMatch">
                <Box>
                  <Typography variant="body2">Unordered Match</Typography>
                  <Typography variant="caption" sx={{ display: 'block', color: colors.grey[400] }}>
                    Every correct answer must appear in the student's answers. Extras are allowed.
                  </Typography>
                </Box>
              </MenuItem>
            </Select>
          </FormControl>

          {/* Min Rows */}
          <TextField
            label="Min Rows"
            type="number"
            size="small"
            value={config.minRows || 1}
            onChange={(e) => updateConfig({ minRows: Math.max(1, parseInt(e.target.value) || 1) })}
            sx={{ width: 90 }}
            inputProps={{ min: 1 }}
          />

          {/* Max Rows */}
          <TextField
            label="Max Rows"
            type="number"
            size="small"
            value={config.maxRows || 10}
            onChange={(e) => updateConfig({ maxRows: Math.max(0, parseInt(e.target.value) || 0) })}
            sx={{ width: 90 }}
            inputProps={{ min: 0 }}
            helperText="0 = no limit"
          />
        </Box>

        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, alignItems: 'center' }}>
          <FormControlLabel
            control={
              <Checkbox
                checked={config.studentCanAddRows !== false}
                onChange={(e) => updateConfig({ studentCanAddRows: e.target.checked })}
              />
            }
            label="Students can add/remove rows"
          />

          {config.inputType === 'text' && (
            <FormControlLabel
              control={
                <Checkbox
                  checked={config.caseSensitive || false}
                  onChange={(e) => updateConfig({ caseSensitive: e.target.checked })}
                />
              }
              label="Case sensitive"
            />
          )}

          {config.inputType === 'mathlive' && config.gradingMode !== 'manual' && (
            <TextField
              label="Tolerance"
              type="number"
              size="small"
              value={config.tolerance || 0}
              onChange={(e) => updateConfig({ tolerance: parseFloat(e.target.value) || 0 })}
              sx={{ width: 100 }}
              inputProps={{ min: 0, step: 0.01 }}
            />
          )}

          {config.inputType === 'mathlive' && (
            <FormControlLabel
              control={
                <Checkbox
                  checked={config.allowTextInput === true}
                  onChange={(e) => updateConfig({ allowTextInput: e.target.checked })}
                />
              }
              label={'Allow text answers (e.g. "no solutions") per row'}
            />
          )}
        </Box>

        {/* Row Labels */}
        <Box sx={{ mt: 2 }}>
          <TextField
            label="Row Labels (comma-separated, optional)"
            size="small"
            fullWidth
            value={rowLabelsText}
            onChange={(e) => handleRowLabelsChange(e.target.value)}
            placeholder="e.g. ①, ②, ③ or Step 1, Step 2, Step 3"
            helperText="Leave empty for no labels. Labels repeat cyclically if fewer than rows."
          />
        </Box>
      </Paper>

      {/* Grading Mode Info */}
      <Box sx={{ mb: 2 }}>
        {config.gradingMode === 'manual' && (
          <Chip label="Manual grading — teacher will grade this question" color="warning" size="small" />
        )}
        {config.gradingMode === 'exactSet' && (
          <Chip label="Exact Set — student must provide ALL correct answers (order doesn't matter)" color="info" size="small" />
        )}
        {config.gradingMode === 'unorderedMatch' && (
          <Chip label="Unordered Match — every correct answer must appear in student's answers" color="info" size="small" />
        )}
      </Box>

      {/* Submittability check — warn the admin if the row-count constraints
          make it impossible for a student to provide enough answers. The
          server's grader filters empty rows before counting, so it's fine
          for a student to have MORE input slots than correct answers; the
          dangerous case is when they can't reach enough rows. */}
      {(() => {
        const correctRowCount = (correctAnswer.rows || [])
          .filter(r => r && r.value && String(r.value).trim()).length;
        if (config.gradingMode === 'manual' || correctRowCount === 0) return null;

        const minRowsValue = config.minRows || 1;
        const maxRowsValue = config.maxRows || 0; // 0 = unlimited
        const studentCanAdd = config.studentCanAddRows !== false;

        const reachable = studentCanAdd
          ? (maxRowsValue === 0 || maxRowsValue >= correctRowCount)
          : (minRowsValue >= correctRowCount);

        if (reachable) return null;

        const fixSuggestion = studentCanAdd
          ? `Bump Max Rows to at least ${correctRowCount} (or 0 for unlimited).`
          : `Either bump Min Rows to ${correctRowCount}, or check "Students can add/remove rows".`;

        return (
          <Alert severity="error" sx={{ mb: 2 }}>
            Students can't reach {correctRowCount} answer{correctRowCount === 1 ? '' : 's'} with the current settings — this question is unsubmittable. {fixSuggestion}
          </Alert>
        );
      })()}

      {/* Correct Answer Editor — only for auto-graded modes */}
      {config.gradingMode !== 'manual' && (
        <Paper sx={{ p: 2, backgroundColor: colors.primary[600] }}>
          <Typography variant="subtitle2" fontWeight="bold" sx={{ mb: 1 }}>
            Correct Answers
          </Typography>
          <Typography variant="caption" sx={{ mb: 2, display: 'block', color: colors.grey[300] }}>
            Enter all correct answers. Each row is one valid answer.
          </Typography>

          {(correctAnswer.rows || []).map((row, index) => {
            const rowType = getRowType(row, config);
            const isMath = rowType === ROW_TYPES.MATH;
            const currentValue = row.value || '';
            const hasValue = !!String(currentValue).trim();
            // Dual-input mutual exclusion (matches MultilineStudent and the
            // plain mathlive `allowTextInput` UI): typing in one input locks
            // the other until cleared.
            const mathReadOnly = dualInput && hasValue && !isMath;
            const textDisabled = dualInput && hasValue && isMath;
            return (
              <Box
                key={row.id}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1,
                  mb: 1,
                  flexWrap: dualInput ? 'wrap' : 'nowrap',
                }}
              >
                {/* Row label */}
                <Typography variant="body2" sx={{ minWidth: 30, color: colors.grey[300] }}>
                  {index + 1}.
                </Typography>

                {dualInput ? (
                  <>
                    <Box sx={{ flex: 1, minWidth: 200 }}>
                      <math-field
                        onInput={(evt) => {
                          if (mathReadOnly) return;
                          updateCorrectRowTyped(row.id, ROW_TYPES.MATH, evt.target.value);
                        }}
                        read-only={mathReadOnly ? true : undefined}
                        // Imperatively sync the math-field's internal value to
                        // the prop. Without this, switching a row from text
                        // back to math (or loading a different question) leaves
                        // the math-field showing a stale value because React
                        // doesn't push value attribute changes into the web
                        // component after mount.
                        ref={(el) => {
                          const expected = isMath ? currentValue : '';
                          if (el && el.getValue && el.getValue() !== expected) {
                            el.setValue(expected);
                          }
                        }}
                        style={{
                          width: '100%',
                          minHeight: '36px',
                          fontSize: '14px',
                          border: '1px solid #ccc',
                          borderRadius: '4px',
                          padding: '4px 8px',
                          backgroundColor: '#fff',
                          opacity: mathReadOnly ? 0.5 : 1,
                        }}
                      />
                    </Box>
                    <Typography variant="caption" sx={{ mx: 0.5 }}>or</Typography>
                    <TextField
                      value={!isMath ? currentValue : ''}
                      onChange={(e) => updateCorrectRowTyped(row.id, ROW_TYPES.TEXT, e.target.value)}
                      disabled={textDisabled}
                      size="small"
                      sx={{ flex: 1, minWidth: 160 }}
                      placeholder="Text answer"
                    />
                  </>
                ) : isMath ? (
                  <Box sx={{ flex: 1 }}>
                    <math-field
                      onInput={(evt) => updateCorrectRow(row.id, evt.target.value)}
                      // Imperative sync — see MultilineReadOnly for the
                      // rationale. Without this, edits to existing correct
                      // answers don't visually update the math-field.
                      ref={(el) => {
                        const expected = row.value || '';
                        if (el && el.getValue && el.getValue() !== expected) {
                          el.setValue(expected);
                        }
                      }}
                      style={{
                        width: '100%',
                        minHeight: '36px',
                        fontSize: '14px',
                        border: '1px solid #ccc',
                        borderRadius: '4px',
                        padding: '4px 8px',
                        backgroundColor: '#fff',
                      }}
                    />
                  </Box>
                ) : (
                  <TextField
                    value={row.value || ''}
                    onChange={(e) => updateCorrectRow(row.id, e.target.value)}
                    size="small"
                    fullWidth
                    placeholder={`Answer ${index + 1}`}
                  />
                )}

                {/* Delete button */}
                <IconButton
                  size="small"
                  onClick={() => removeCorrectRow(row.id)}
                  color="error"
                >
                  <DeleteIcon fontSize="small" />
                </IconButton>
              </Box>
            );
          })}

          <Button
            startIcon={<AddIcon />}
            onClick={addCorrectRow}
            size="small"
            sx={{ mt: 1 }}
          >
            Add Correct Answer
          </Button>
        </Paper>
      )}
    </Box>
  );
};

export default MultilineAdmin;
